import { motion, usePresence } from 'framer-motion';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Avatar, Card, tokens } from '@treams/treams-ui';

import useTalentReviewDropStyle from '../hooks/useTalentReviewDropStyle';
import TalentReviewAvatarTooltip from './TalentReviewTooltip';

/** Same curve as the grid avatars so a cleared avatar glides back into this list. */
const LAYOUT_TRANSITION = {
	duration: 0.5,
	ease: [0.075, 0.82, 0.165, 1] as const,
};

const COLLAPSE_TRANSITION = {
	duration: 0.35,
	ease: [0.22, 1, 0.36, 1] as const,
};

/* Animates the card between `height: auto` and 0, fading in and out along the way. */
const Collapse = styled(motion.div)<{ $collapsing?: boolean }>`
	overflow: ${(props) => (props.$collapsing ? 'hidden' : 'visible')};
`;

/* Avatars glide in from the grid; they must not be clipped by the card. */
const UnplacedCard = styled(Card)`
	overflow: visible;
`;

/** Hovered avatars pop up slightly, like the grid ones. */
const HOVER_SCALE = 1.05;
const HOVER_SPRING = { type: 'spring' as const, stiffness: 400, damping: 17 };

/* Lifts a moving avatar above the grid cells, which paint later in the DOM. */
const MotionAvatar = styled(motion.div)`
	position: relative;
	z-index: 1;
	line-height: 0;
`;

const Title = styled.h3`
	margin: 0;
	color: ${tokens.text.primary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.h3}px;
	font-weight: 550;
	line-height: ${tokens.typography.lineHeights.heading};
`;

const Description = styled.p`
	margin: 0;
	color: ${tokens.text.secondary};
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.body};
`;

const Avatars = styled(Flex)`
	align-items: center;
	gap: ${tokens.gap.element}px;
	flex-wrap: wrap;
	min-height: 40px;
`;

const DraggableAvatar = styled(Box)<{ $dragging?: boolean }>`
	border-radius: ${tokens.radius.full}px;
	cursor: grab;
	box-shadow: ${(props) => (props.$dragging ? tokens.shadow.md : 'none')};

	&:active {
		cursor: grabbing;
	}
`;

/**
 * Card with the people that are not on the grid yet, and the drop target for
 * taking someone off it. Render it inside an `AnimatePresence`: it grows in and
 * collapses out. Not rendered once everyone is placed (see `TalentReviewV2`).
 * @param props Component props.
 * @param props.users People without a placement on the grid.
 * @param props.readonly Disables dragging.
 * @param props.selectedUserID The selected person. Unlike the grid, the others stay
 * visible here: clicking one selects them, clicking again deselects, dragging still places them.
 * @param props.dragging True while any drag is in progress; layout animations pause so they do not fight dnd.
 * @param props.onSelectUser Called when an avatar is clicked.
 */
export default function TalentReviewUnplaced(props: {
	users: graphql.User[];
	readonly?: boolean;
	/** While someone is selected in the sidebar, everyone else is faded out and cannot be dragged. */
	selectedUserID?: string;
	/** True while any drag is in progress; layout animations are paused so they do not fight dnd. */
	dragging?: boolean;
	onSelectUser?: (userID: string) => void;
}) {
	const { t } = useTranslation();
	const [isPresent, safeToRemove] = usePresence();
	const dropStyle = useTalentReviewDropStyle();

	return (
		<Collapse
			$collapsing={!isPresent}
			initial={{ height: 0, marginBottom: 0, opacity: 0 }}
			animate={{
				height: isPresent ? 'auto' : 0,
				marginBottom: isPresent ? tokens.gap.section : 0,
				opacity: isPresent ? 1 : 0,
			}}
			transition={COLLAPSE_TRANSITION}
			onAnimationComplete={() => {
				if (!isPresent) {
					safeToRemove?.();
				}
			}}
		>
			<Droppable
				droppableId="team"
				type="USER"
				direction="horizontal"
				isDropDisabled={props.readonly}
			>
				{(provided) => (
					<UnplacedCard p={0}>
						<Box p={3} ref={provided.innerRef} {...provided.droppableProps}>
							<motion.div
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={COLLAPSE_TRANSITION}
							>
								<Title>{t('talentReviews.v2.unplaced.title', { count: props.users.length })}</Title>
								<Box mt={2}>
									<Description>{t('talentReviews.v2.unplaced.description')}</Description>
								</Box>
							</motion.div>
							{/* Where dropped people land; the drop animation aims here, not at the title. */}
							<Avatars mt={3} data-drop-area>
								{props.users.map((user, index) => (
									<Draggable
										key={user.id}
										draggableId={user.id}
										index={index}
										isDragDisabled={props.readonly}
									>
										{(dragProvided, dragSnapshot) => (
											<DraggableAvatar
												ref={dragProvided.innerRef}
												$dragging={dragSnapshot.isDragging}
												{...dragProvided.draggableProps}
												{...dragProvided.dragHandleProps}
												style={{
													...dragProvided.draggableProps.style,
													...dropStyle(
														dragProvided,
														dragSnapshot,
														'team',
														props.selectedUserID === user.id,
													),
												}}
												onClick={() => props.onSelectUser?.(user.id)}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														event.preventDefault();
														props.onSelectUser?.(user.id);
													}
												}}
											>
												<MotionAvatar
													layout={props.dragging ? false : 'position'}
													layoutId={props.dragging ? undefined : `talent-review-person-${user.id}`}
													layoutCrossfade={false}
													transition={LAYOUT_TRANSITION}
												>
													<TalentReviewAvatarTooltip
														user={user}
														enabled={!props.dragging && !dragSnapshot.isDragging}
													>
														<Avatar.Ring
															tone={props.selectedUserID === user.id ? 'brand' : undefined}
															width={3}
															hoverTone={
																!props.dragging &&
																!dragSnapshot.isDragging &&
																props.selectedUserID !== user.id
																	? 'brand'
																	: undefined
															}
															hoverWidth={2}
															whileHover={
																!props.dragging && !dragSnapshot.isDragging
																	? { scale: HOVER_SCALE }
																	: undefined
															}
															transition={{ scale: HOVER_SPRING }}
														>
															<Avatar size={40} user={user} />
														</Avatar.Ring>
													</TalentReviewAvatarTooltip>
												</MotionAvatar>
											</DraggableAvatar>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</Avatars>
						</Box>
					</UnplacedCard>
				)}
			</Droppable>
		</Collapse>
	);
}
